import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@arcadeai/design-system/components/ui/atoms/resizable";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tabs";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { SquareCode, Wrench } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useMediaQuery } from "usehooks-ts";
import type { z } from "zod";
import { useShallow } from "zustand/shallow";
import { toolkitCollectionQueryOptions } from "@/app/tools/api/get-toolkit-collection";
import { extractToolName } from "@/app/tools/utils";
import { useSession } from "@/lib/auth-client";
import { useResourceScope } from "@/lib/resource-context";
import { FormPanel } from "./components/form/form-panel";
import { ResponsePanel } from "./components/panel/response";
import { useArcadeClient } from "./hooks/use-arcade-client";
import { useFormValidation } from "./hooks/use-form-validation";
import { usePlaygroundStore } from "./hooks/use-store";
import { useToolInitialization } from "./hooks/use-tool-initialization";
import { extractParameterValues, generateParameterSchema } from "./utils";

export function PlaygroundExecuteTool() {
  const { data: session } = useSession();
  const principalId = session?.user?.rawEmail ?? session?.user?.email;
  const isCompact = useMediaQuery("(max-width: 1023px)");
  const [activeTab, setActiveTab] = useState("tool");

  const scope = useResourceScope();
  const { data: toolkitCollection } = useSuspenseQuery(
    toolkitCollectionQueryOptions(scope)
  );
  const tools = toolkitCollection?.tools;

  const { toolId, input } = useSearch({
    from: "/_authenticated/orgs/$organizationId/projects/$projectId/playground/execute",
  });
  const navigate = useNavigate();

  const {
    selectedTool,
    setSelectedTool,
    parameters,
    toolInputByFqn,
    setToolInput,
  } = usePlaygroundStore(
    useShallow((state) => ({
      selectedTool: state.selectedTool,
      setSelectedTool: state.setSelectedTool,
      parameters: state.parameters,
      toolInputByFqn: state.toolInputByFqn,
      setToolInput: state.setToolInput,
    }))
  );

  const schema = useMemo(
    () => generateParameterSchema(parameters),
    [parameters]
  );

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    mode: "onChange",
    // Don't use route input as defaultValues - let useToolInitialization handle it
    // This prevents default values from one tool bleeding into others
    defaultValues: {},
  });

  useToolInitialization({
    tools,
    toolId,
    urlInput: input,
    form,
  });

  const { executeTool } = useArcadeClient({ userId: principalId });
  const { isFormValid, tooltipMessage } = useFormValidation(form, parameters);

  // Extract specific form methods to avoid unstable form object in dependency arrays
  const { reset, getValues, handleSubmit: formHandleSubmit } = form;

  const handleToolSelection = useCallback(
    (selection: { selectedToolkits: string[]; selectedTools: string[] }) => {
      const toolFqn = selection.selectedTools[0];
      if (!toolFqn) {
        return;
      }

      if (selectedTool) {
        setToolInput(selectedTool.fully_qualified_name, getValues());
      }

      const tool = tools?.find((t) => t.fully_qualified_name === toolFqn);
      if (!tool) {
        return;
      }

      setSelectedTool(tool);
      navigate({
        to: ".",
        search: { toolId: extractToolName(tool), input: {} },
      });

      const savedValues = toolInputByFqn[tool.fully_qualified_name];
      reset(extractParameterValues(tool, savedValues));
    },
    [
      selectedTool,
      setToolInput,
      getValues,
      tools,
      setSelectedTool,
      navigate,
      toolInputByFqn,
      reset,
    ]
  );

  const handleSubmit = formHandleSubmit(async (values) => {
    if (isCompact) {
      setActiveTab("output");
    }
    await executeTool(values);
  });

  if (isCompact) {
    return (
      <Tabs
        className="flex h-full flex-col gap-2"
        onValueChange={setActiveTab}
        value={activeTab}
      >
        <TabsList className="w-full">
          <TabsTrigger className="flex-1 gap-2" value="tool">
            <Wrench className="h-4 w-4" />
            Setup
          </TabsTrigger>
          <TabsTrigger className="flex-1 gap-2" value="output">
            <SquareCode className="h-4 w-4" />
            Output
          </TabsTrigger>
        </TabsList>
        <TabsContent className="min-h-0 flex-1" value="tool">
          <FormPanel
            form={form}
            isFormValid={isFormValid}
            onSubmit={handleSubmit}
            onToolSelection={handleToolSelection}
            showHeader={false}
            toolkitCollection={toolkitCollection}
            tooltipMessage={tooltipMessage}
          />
        </TabsContent>
        <TabsContent className="min-h-0 flex-1" value="output">
          <ResponsePanel
            expectedOutput={selectedTool?.output?.description}
            isExecuteDisabled={!isFormValid}
            onBackToTool={() => setActiveTab("tool")}
            onExecute={handleSubmit}
            tooltipMessage={tooltipMessage}
          />
        </TabsContent>
      </Tabs>
    );
  }

  return (
    <ResizablePanelGroup className="h-full gap-4" orientation="horizontal">
      <ResizablePanel defaultSize={50} minSize={30}>
        <FormPanel
          form={form}
          isFormValid={isFormValid}
          onSubmit={handleSubmit}
          onToolSelection={handleToolSelection}
          toolkitCollection={toolkitCollection}
          tooltipMessage={tooltipMessage}
        />
      </ResizablePanel>
      <ResizableHandle withHandle />
      <ResizablePanel defaultSize={50} minSize={30}>
        <ResponsePanel
          expectedOutput={selectedTool?.output?.description}
          isExecuteDisabled={!isFormValid}
          onExecute={handleSubmit}
          tooltipMessage={tooltipMessage}
        />
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}
